import { useState, useEffect, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import Questionary from './Questionary'
import Button from 'components/UI/Button'
import Pagination from 'components/pagination'
import TestProcessWarning from './TestProcessWarning'

import {
  cancelTest,
  lastPassedCategory,
  submitTest,
  toggleFullscreen,
} from 'routing/fetchingfunction/user/user.action'
import {
  selectCurrentTest,
  selectLoadingCancelTest,
  selectLoadingSubmitTest,
} from '../../../../../routing/fetchingfunction/user/user.reducer'

import { enterFullscreen, exitFullscreen, getIsFullscreen } from '../../../../../helpers/fullscreen'

import { testPassedStatuses, tutorRegistrationTests } from 'constants'

import checkmarkOutline from 'assets/icon/checkmark-outline.svg'
import cancelOutline from 'assets/icon/cancel-outline.svg'

const TestProcess = ({
  onTestSubmit,
  onCancelTest,
  onExitTest,
  exitConfirmation,
  proctoringStatus,
  setProctoringStatus,
  warningMessage,
  setWarningMessage,
  onSnapshot,
  snapshots,
  setSnapshots,
  onProctorStop,
  showWarning,
  setShowWarning,
  onReset,
  streamActivities,
  onRebind,
  videoRef,
  canvasRef,
  proctorViolationsStatus,
  setSubmittingTest,

  resetStep,
}) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const currentTest = useSelector(selectCurrentTest)
  const cancelLoading = useSelector(selectLoadingCancelTest)
  const submitLoading = useSelector(selectLoadingSubmitTest)

  const { selectedCategories, activeCategoryIndex } = useSelector((state) => state.user)

  const [activeCategory, setActiveCategory] = useState(null)
  const [activeQuestionNumber, setActiveQuestionNumber] = useState(0)
  const [answeredQuestions, setAnsweredQuestions] = useState([])
  const [selectedAnswer, setSelectedAnswer] = useState(null)
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false)
  const failsCount = useRef(0)
  const failMouseOutCount = useRef(0)
  const isFullscreening = useRef(false)

  const handleCancelTest = async (retried = false) => {
    if (!showCancelConfirmation) {
      setShowCancelConfirmation(true)
      return
    }

    if (
      !cancelLoading &&
      !retried &&
      (!currentTest || !selectedCategories?.[activeCategoryIndex])
    ) {
      return setTimeout(() => {
        handleCancelTest(true)
      }, 3000)
    }

    await handleCancelCurrentTest()
    setShowCancelConfirmation(false)
  }

  const handleCancelTestFailedRules = async (retried = false) => {
    if (
      !cancelLoading &&
      !retried &&
      (!currentTest || !selectedCategories?.[activeCategoryIndex])
    ) {
      return setTimeout(() => {
        handleCancelTestFailedRules(true)
      }, 3000)
    }

    await handleCancelCurrentTest()
  }

  useEffect(() => {
    console.log(streamActivities, canvasRef, videoRef)
    const video = videoRef.current
    const canvas = canvasRef.current
    const newstream = streamActivities.video

    if (!video || !canvas) return
    onRebind()
    // if (video.srcObject !== newstream) {
    //   video.srcObject = newstream
    //   video.onloadedmetadata = () => {
    //     video.play().catch((err) => console.log('Error playing video', err))
    //     if (video && canvas) {
    //       onRebind(video, canvas)
    //     }
    //   }
    // }
  }, [])

  useEffect(() => {
    if (activeQuestionNumber % 2 == 0 && activeQuestionNumber > 0) onSnapshot()
  }, [activeQuestionNumber])

  useEffect(() => {
    const findCategory = tutorRegistrationTests.find(
      (test) => test.slug === selectedCategories[activeCategoryIndex]?.category_slug
    )
    setActiveCategory(findCategory)
  }, [activeCategoryIndex])

  const handleSubmitTest = async () => {
    setSubmittingTest(true)
    const nextStepIndex = activeCategoryIndex === selectedCategories.length - 1 ? 1 : -1
    const video = await onProctorStop(nextStepIndex)
    let proctorViolation = proctorViolationsStatus.TOTAL >= 5 ? 'flagged' : 'success'

    const highestKey = Object.entries(proctorViolationsStatus).reduce((maxKey, [key, value]) => {
      return value > proctorViolationsStatus[maxKey] ? key : maxKey
    }, Object.keys(proctorViolationsStatus)[0])

    const sendData = {
      proctoring_disqualified_reason: highestKey,
      proctoring_status: proctorViolation,
      test_id: currentTest.test_id,
      category_slug: selectedCategories[activeCategoryIndex].category_slug,
      answers: answeredQuestions,
      snapshots: snapshots,
      category_name: selectedCategories[activeCategoryIndex].category_name,

      video_record: proctoringStatus.proctoring_status !== 'success' ? video : null,
    }

    dispatch(submitTest(sendData))
      .then((res) => {
        if (!res.payload || res.error) {
          return
        }
        setSnapshots([])
        setProctoringStatus({
          proctoring_disqualified_reason: 'N/A',
          proctoring_status: 'success',
        })

        const passedStatus =
          res.payload.result === 'pending'
            ? testPassedStatuses.FLAGGED
            : res.payload.is_passed
              ? testPassedStatuses.PASSED_PARTIAL
              : testPassedStatuses.FAILED_PARTIAL
        onTestSubmit(passedStatus)

        setTimeout(() => {
          exitFullscreen(() => dispatch(toggleFullscreen(false)))
        }, 0)
      })
      .finally(() => setSubmittingTest(false))
    // }

    dispatch(lastPassedCategory(selectedCategories[activeCategoryIndex]))
  }

  const handleCancelCurrentTest = async () => {
    setSubmittingTest(true)
    setShowWarning(true)
    setShowWarning('Test has been canceled')

    const nextStepIndex = activeCategoryIndex === selectedCategories.length - 1 ? 1 : -1
    const video = await onProctorStop(nextStepIndex)

    const sendData = {
      test_id: currentTest.test_id,
      category_slug: selectedCategories[activeCategoryIndex].category_slug,
      snapshots: snapshots,
      category_name: selectedCategories[activeCategoryIndex].category_name,
    }
    const passedStatus = testPassedStatuses.CANCELED_PARTIAL

    dispatch(cancelTest(sendData))
      .unwrap()
      .then((res) => {
        setTimeout(() => {
          onTestSubmit(passedStatus)
          exitFullscreen(() => dispatch(toggleFullscreen(false)))
        }, 0)
      })
      .catch((err) => {
        onTestSubmit(passedStatus)
        setSubmittingTest(true)
        resetStep()
      })

    // dispatch(lastPassedCategory(selectedCategories[activeCategoryIndex]))
  }

  const handlePageChange = (_, page) => {
    if (!page) return
    setActiveQuestionNumber(page - 1)
    setSelectedAnswer(null)
  }

  const handleResetWarning = () => {
    if (!getIsFullscreen()) {
      isFullscreening.current = true

      enterFullscreen()
        .then(() => {
          setTimeout(() => {
            onReset()

            dispatch(toggleFullscreen(true))

            isFullscreening.current = false
          }, 800)
        })
        .catch((e) => {
          console.log(e)
        })
      // .finally(() => {
      //   setTimeout(() => {
      //     setShowWarning(false)
      //     setWarningMessage(null)
      //     isFullscreening.current = false
      //   }, 800)
      // })
    } else {
      onReset()
    }
  }
  const handleFullScreenChange = () => {
    if (!getIsFullscreen()) {
      failsCount.current += 1

      if (failsCount.current <= 2) {
        setShowWarning(true)
        setWarningMessage(
          'Warning: Do not change tabs or close fullscreen mode. Another attempt to change tabs may result in the failure of this test.'
        )
      } else if (!showWarning && failsCount.current > 2) {
        console.log('[TEST PROCESS] Handle Cancel Test', failsCount.current)
        handleCancelTestFailedRules()
      }
    }
  }

  const handleMouseLeave = (event) => {
    if (
      !isFullscreening.current &&
      (event.clientY <= 0 ||
        event.clientX <= 0 ||
        event.clientX >= window.innerWidth ||
        event.clientY >= window.innerHeight)
    ) {
      failMouseOutCount.current += 1

      if (failMouseOutCount.current <= 2) {
        setShowWarning(true)
        setWarningMessage(
          'Warning: Do not Leave Test screen. Another attempt may result in the failure of this test.'
        )
      } else if (!showWarning && failMouseOutCount.current > 2) {
        handleCancelTest()
      }
    }
  }
  useEffect(() => {
    handleFullScreenChange()

    document.addEventListener('fullscreenchange', handleFullScreenChange)
    window.addEventListener('mouseout', handleMouseLeave)

    return () => {
      document.removeEventListener('fullscreenchange', handleFullScreenChange)
      window.removeEventListener('mouseout', handleMouseLeave)
    }
  }, [currentTest, cancelLoading, showWarning])

  return (
    <div>
      <div className="z-10 m-auto relative pointer-events-none border-2 border-green-600 bg-gray-100 overflow-hidden left-0 w-20 h-20 rounded-full flex items-center justify-center">
        <canvas
          ref={canvasRef}
          className=" absolute top-0 left-0 hey z-10 h-full w-full"
          style={{ position: 'absolute', top: 0, left: 0 }}
        />
        <video
          ref={videoRef}
          className="object-cover -mx-4 proctor-element"
          autoPlay
          style={{ marginTop: '0px', height: '100%', width: '100%' }}
        ></video>
      </div>
      <div className="flex items-center justify-between md:gap-20 gap-10  w-full pt-2">
        <h3 className="text-white text-[24px] leading-[34px] font-semibold">
          Fill in your answers!
        </h3>

        <div className="flex items-center justify-end">
          <Button
            className="mr-2 inline-flex items-center justify-center"
            variant="success"
            loadMessage={'Submitting'}
            isLoading={submitLoading}
            onClick={() => {
              if (currentTest.questions.length !== answeredQuestions.length) {
                setShowWarning(true)
                setWarningMessage('Please answer all the questions before submitting')
              } else {
                handleSubmitTest()
              }
            }}
          >
            <div className="w-6 h-6 mr-[6px] inline-flex items-center justify-center">
              <img className="block" src={checkmarkOutline} alt="checkmark-outline" />
            </div>
            <span>Finish Test</span>
          </Button>

          <Button
            className="inline-flex items-center justify-center"
            variant="error"
            isLoading={cancelLoading}
            onClick={handleCancelTest}
          >
            <div className="w-6 h-6 mr-[6px] inline-flex items-center justify-center">
              <img className="block" src={cancelOutline} alt="cancel-outline" />
            </div>
            <span>Cancel Test</span>
          </Button>
        </div>
      </div>

      {currentTest?.questions && (
        <>
          <Questionary
            activeQuestionNumber={activeQuestionNumber}
            setActiveQuestionNumber={setActiveQuestionNumber}
            answeredQuestions={answeredQuestions}
            setAnsweredQuestions={setAnsweredQuestions}
            selectedAnswer={selectedAnswer}
            setSelectedAnswer={setSelectedAnswer}
            questions={currentTest.questions}
            onCancelTest={handleSubmitTest}
            activeCategory={activeCategory}
          />
          <Pagination
            count={currentTest?.questions?.length}
            answeredQuestions={answeredQuestions}
            onChange={handlePageChange}
            page={activeQuestionNumber + 1}
            total={currentTest.questions.length}
          />
        </>
      )}

      {showCancelConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full">
            <h3 className="text-xl font-bold text-primary-100 mb-4">Cancel Test?</h3>
            <p className="text-gray-700 mb-6">
              Are you sure you want to cancel this test? You will have to wait 30 days before
              retaking it.
            </p>
            <div className="flex justify-end space-x-4">
              <Button variant="secondary" onClick={() => setShowCancelConfirmation(false)}>
                Continue Test
              </Button>
              <Button variant="error" onClick={handleCancelTest}>
                Cancel Test
              </Button>
            </div>
          </div>
        </div>
      )}

      {exitConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full">
            <h3 className="text-xl font-bold text-primary-100 mb-4">Exit Test</h3>
            <p className="text-gray-700 mb-6 text-xl">You voilated the rules</p>
            <p className="text-gray-700 mb-6">{warningMessage}</p>
            <div className="flex justify-end space-x-4">
              <Button
                variant="error"
                onClick={() => {
                  if (onExitTest) {
                    onExitTest()
                  } else {
                    navigate(-1)
                  }
                  exitFullscreen(() => dispatch(toggleFullscreen(false)))
                }}
              >
                Exit Test
              </Button>
            </div>
          </div>
        </div>
      )}

      <TestProcessWarning
        open={showWarning}
        onClose={handleResetWarning}
        warningMessage={warningMessage}
      />
    </div>
  )
}

export default TestProcess
